import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { EnqueteType, resetELearningPage } from 'src/actions/eLearning';
import { IMessage } from 'src/actions/message';
import {
	initQuestionnaire,
	IQuestionnaireBlock,
} from 'src/actions/questionnaire';
import MessageDialog from 'src/components/MessageDialog';
import { IRootState } from 'src/reducers';
import { IFetchELearningProgressParam } from 'src/scenario/ELearningScenarioFactory';

import { IELearningCommonProps } from './ELearning';
import QuestionnaireGrid from './QuestionnaireGrid';
import QuestionnaireNormal from './QuestionnaireNormal';
import QuestionnaireWizard from './QuestionnaireWizard';

interface IState {
	errorMessage: IMessage[];
	openErrorDialog: boolean;
}

export interface IQuestionnaireProps extends IELearningQuestionnaireProps {
	id: string;
	onReported: () => void;
	unAnsweredCheck: (
		mustAnswerFlg: number,
		updatedBlocks: IQuestionnaireBlock[]
	) => boolean;
	flg: number;
	onlyQuestionFlg: boolean;
}

const questionnaireMap: {
	[T in EnqueteType]: React.ComponentType<IQuestionnaireProps>
} = {
	'1': QuestionnaireNormal,
	'2': QuestionnaireGrid,
	'3': QuestionnaireWizard,
};

/**
 * eラーニングアンケート
 */
export class ELearningQuestionnaireComponent extends React.Component<
	IProps,
	IState
> {
	constructor(props: IProps) {
		super(props);

		this.state = {
			errorMessage: [],
			openErrorDialog: false,
		};
	}

	/**
	 * Lifecycle Methods
	 */
	public componentDidMount() {
		const {
			careerID,
			classID,
			init,
			preview: isPreview,
			userId,
		} = this.props;

		// 初期化アクション
		init({
			careerID,
			classID,
			isPreview,
			userId,
		});
	}

	public render() {
		if (!this.props.currentTreeItem) {
			return;
		}

		if (!(this.props.currentTreeItem.enqueteType in questionnaireMap)) {
			return <div />;
		}

		const Questionnaire =
			questionnaireMap[this.props.currentTreeItem.enqueteType];

		return (
			<React.Fragment>
				<Questionnaire
					accessFlg={this.props.accessFlg}
					careerID={this.props.careerID}
					classID={this.props.classID}
					id={this.props.currentTreeItem.id}
					onReported={this.props.resetELearningPage}
					outOfPeriodFlg={this.props.outOfPeriodFlg}
					preview={this.props.preview}
					unAnsweredCheck={this.unAnsweredCheck}
					flg={this.props.flg}
					onlyQuestionFlg={false}
					handlePrev={this.props.handlePrev}
					handleNext={this.props.handleNext}
					onRef={this.props.onRef}
					updateState={this.props.updateState}
				/>
				<MessageDialog
					open={this.state.openErrorDialog}
					onClose={this.closeErrorDialog}
					contents={this.state.errorMessage}
					showCancelButton={false}
					onClickOk={this.closeErrorDialog}
				/>
			</React.Fragment>
		);
	}

	/**
	 * 未回答チェック
	 */
	protected unAnsweredCheck = (
		mustAnswerFlg: number,
		updatedBlocks: IQuestionnaireBlock[]
	): boolean => {
		let unanswerdBlockQuestionList: string[] = [];
		updatedBlocks.map(block => {
			const unanswerdQuestion = block.questionnaires.filter(
				questionnaireInfo => {
					return (
						questionnaireInfo.mustFlg === 1 &&
						((questionnaireInfo.questionnaireType === '3' &&
							(questionnaireInfo.options &&
								!questionnaireInfo.options.some(
									opt => opt.selected === true
								))) ||
							(questionnaireInfo.questionnaireType === '4' &&
								(questionnaireInfo.options &&
									questionnaireInfo.options[0].replyText ===
										'')) ||
							(questionnaireInfo.questionnaireType !== '3' &&
								questionnaireInfo.questionnaireType !== '4' &&
								questionnaireInfo.selectedReply === 0))
					);
				}
			);

			const unanswerdBlockQuestion: string[] = unanswerdQuestion.map(
				item => {
					return `${block.blockName}-${item.questionnaireNo.substring(
						0,
						item.questionnaireNo.length - 1
					)}`;
				}
			);
			unanswerdBlockQuestionList = [
				...unanswerdBlockQuestionList,
				...unanswerdBlockQuestion,
			];
		});

		if (unanswerdBlockQuestionList.length === 0) {
			return false;
		} else {
			let content = unanswerdBlockQuestionList.toString();
			content = content.replace(/,/g, '<br/>');
			const message: IMessage = {
				content: 'LMCM_E_15511',
				replacements: [
					'<span style="color:#FF0000;font-weight:bold;">',
					'</span>',
					content,
				],
			};
			this.setState({
				errorMessage: [message],
				openErrorDialog: true,
			});
			return true;
		}
	};

	/**
	 * エラーダイアログオープン
	 */
	protected openErrorDialog = () => {
		this.setState({ openErrorDialog: true });
	};

	/**
	 * エラーダイアログクローズ
	 */
	protected closeErrorDialog = () => {
		this.setState({ openErrorDialog: false });
	};
}

export const mapStateToProps = (state: IRootState) => ({
	currentTreeItem: state.eLearning.currentTreeItem,
	/** ユーザID */
	userId: state.session.userAttr.userId,
});

export const mapDispatchToProps = (dispatch: Dispatch) => ({
	/** 初期化 */
	init: (payload: IFetchELearningProgressParam) =>
		dispatch(initQuestionnaire(payload)),
	resetELearningPage: () => dispatch(resetELearningPage({})),
});

export interface IELearningQuestionnaireProps extends IELearningCommonProps {
	accessFlg: number;
	flg: number;
	handleNext: () => void;
	handlePrev: () => void;
	onRef: (view: object) => void;
	updateState: () => void;
}

const mergeProps = (
	state: IStateProps,
	events: IDispatchProps,
	ownProps: IOwnProps
) => ({
	...state,
	...events,
	...ownProps,
});

type IStateProps = ReturnType<typeof mapStateToProps>;
type IDispatchProps = ReturnType<typeof mapDispatchToProps>;
type IOwnProps = IELearningQuestionnaireProps;
type IProps = ReturnType<typeof mergeProps>;

const ELearningQuestionnaire = connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(ELearningQuestionnaireComponent);
ELearningQuestionnaire.displayName = 'ELearningQuestionnaire';

export default ELearningQuestionnaire;



// WEBPACK FOOTER //
// ./src/containers/ELearningQuestionnaire.tsx